import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import { PiramidalService } from '@core/services/piramidal.service';
import { environment } from '@environments/environment';
import { HttpClient } from '@angular/common/http';
import { IHighlighted } from './interfaces/IHiglhlighted';
import { IOffers } from './interfaces/IOffer';
import { ICategory } from './interfaces/ICategory';
import { ILatestNews } from './interfaces/ILatestNews';
import { ProductsService } from '@core/services/products.service';
import { Product } from '@core/models/piramide/Product';

@Injectable({
  providedIn: 'root',
})
export class CarouselService {
  constructor(
    private _productsService: ProductsService,
    private piramidalService: PiramidalService,
    private http: HttpClient,
  ) {}
  private productsInList$ = this._productsService.products$;
  private productsBannerPromo = new BehaviorSubject<any>([]);
  public $productsBannerPromo = this.productsBannerPromo.asObservable();
  private newHome = environment.apiStatic;

  public getHighlights(): Observable<any[]> {
    const highlights$ = this.http.get<IHighlighted[]>(
      `${this.newHome}home_carrusel_producto_destacado`,
    );
    const subcategoriesGroupers$ = this.piramidalService.subcategoriesGroupers.observable;

    return combineLatest([highlights$, this.productsInList$, subcategoriesGroupers$]).pipe(
      map(([highlights, productsInList, groupers]) => {
        if (highlights && productsInList && groupers) {
          return highlights
            .map((highlight) => ({
              ...highlight,
              productos: highlight.productos
                ?.map((producto) => {
                  let product = productsInList.find(
                    (productInList) => productInList.id_producto === producto,
                  );
                  if (product) {
                    product['ordenProducto'] = highlight.ordenAgrupador;
                    return product;
                  } else {
                    return null;
                  }
                })
                .filter((product) => product !== null),
              agrupador: groupers.find((grouper) => grouper.id == highlight.idAgrupador),
            }))
            .sort((a, b) => a.ordenAgrupador - b.ordenAgrupador);
        }
      }),
    );
  }

  public getCategories(): Observable<ICategory[]> {
    const categories$ = this.http.get<ICategory[]>(`${this.newHome}home_carrusel_agrupadores`);
    const groupers$ = this.piramidalService.subcategoriesGroupers.observable;

    return combineLatest([categories$, groupers$]).pipe(
      map(([categories, groupers]) => {
        if (categories && groupers) {
          return categories.map((category) => ({
            ...category,
            agrupador: groupers.find((grouper) => grouper.id === category.id_agrupador),
          }));
        }
      }),
    );
  }

  public getOffers(): Observable<IOffers> {
    const offers$ = this.http.get<IOffers>(`${this.newHome}home_productos_rebajados`);

    return combineLatest([offers$, this.productsInList$]).pipe(
      map(([offers, products]) => {
        if (offers && products) {
          offers.productos = offers.productos
            .map((productOffer) =>
              products.find((productInList) => productOffer === productInList.id_producto),
            )
            .filter((productOffer) => productOffer && this.isProductInDiscount(productOffer));
          return offers;
        }
        return undefined;
      }),
    );
  }

  private isProductInDiscount(product: Product): boolean {
    if (!product.precioEspecialAnterior) return false;
    return product.id_categoria === 55 && product.precioEspecialCombo ?
        product.precioEspecialCombo > product.precioEspecial
      : product.precioEspecialAnterior > product.precioEspecial;
  }

  public getLatestNews(): Observable<ILatestNews[]> {
    const latestNews$ = this.http.get<ILatestNews[]>(`${this.newHome}home_productos_novedad`);

    return combineLatest([latestNews$, this.productsInList$]).pipe(
      map(([latestNewsArr, productsInList]) => {
        if (latestNewsArr && productsInList) {
          return latestNewsArr.map((latestNews) => ({
            ...latestNews,
            producto: {
              ...productsInList.find(
                (productInList) => productInList.id_producto === latestNews.id_producto,
              ),
              decoratedName: latestNews.titulo,
            },
          }));
        }
      }),
    );
  }
}
